import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Select, Button, Modal, Input, InputNumber, message, Checkbox, Empty, Tooltip, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import api, { getUser } from '../services/auth';

const ResponsiveGridLayout = WidthProvider(Responsive);

const LAYOUT_KEY = 'stats_widget_layouts';

const SIZE_MAP = {
  small: { w: 1, h: 2, label: '小 (1×1)' },
  large: { w: 2, h: 4, label: '大 (2×2)' },
  wide: { w: 2, h: 2, label: '长 (2×1)' },
  thin: { w: 1, h: 1, label: '扁 (1×0.5)' },
};

const CHART_TYPES = [
  { value: 'number', label: '数值' },
  { value: 'pie', label: '饼图' },
  { value: 'line', label: '折线图' },
  { value: 'bar', label: '柱状图' },
];

const STAT_TYPES = [
  { value: 'count', label: '次数' },
  { value: 'sum', label: '求和' },
  { value: 'avg', label: '平均值' },
  { value: 'min', label: '最小值' },
  { value: 'max', label: '最大值' },
];

const TIME_RANGES = [
  { value: 'today', label: '当天' },
  { value: '7', label: '近7天' },
  { value: '14', label: '近14天' },
  { value: '30', label: '近30天' },
  { value: '90', label: '近90天' },
  { value: 'custom', label: '自定义' },
];

const COMPARE_MODES = [
  { value: 'none', label: '无' },
  { value: 'mom', label: '环比（上一周期）' },
  { value: 'yoy_year', label: '同比（去年）' },
  { value: 'yoy_month', label: '同比（上月）' },
  { value: 'yoy_week', label: '同比（上周）' },
];

const COLORS = ['#007AFF', '#34C759', '#FF9500', '#FF3B30', '#AF52DE', '#5AC8FA', '#FF2D55', '#5856D6'];

function loadJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
}
function saveJson(key, data) {
  localStorage.setItem(key, JSON.stringify(data));
}

function getDirectChild(nodePath, projectName) {
  if (!nodePath || !projectName) return '';
  const prefix = projectName + '/';
  if (!nodePath.startsWith(prefix)) return '';
  return nodePath.substring(prefix.length).split('/')[0] || '';
}

function getDaysFromTimeRange(timeRange, customDays) {
  if (timeRange === 'today') return 0;
  if (timeRange === 'custom') return parseInt(customDays) || 7;
  return parseInt(timeRange);
}

function filterByTimeRange(records, timeRange, customDays) {
  const now = new Date();
  let start;
  if (timeRange === 'today') {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  } else {
    const days = getDaysFromTimeRange(timeRange, customDays);
    start = new Date(now.getTime() - days * 86400000);
  }
  return records.filter(r => new Date(r.recorded_at) >= start);
}

function getCompareRange(timeRange, compareMode, customDays) {
  if (compareMode === 'none') return null;
  const now = new Date();
  const days = getDaysFromTimeRange(timeRange, customDays);

  if (timeRange === 'today') {
    if (compareMode === 'mom') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { start, end };
    }
    if (compareMode === 'yoy_year') {
      const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      const end = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() + 1);
      return { start, end };
    }
    if (compareMode === 'yoy_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      const end = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() + 1);
      return { start, end };
    }
    if (compareMode === 'yoy_week') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      return { start, end };
    }
  }

  if (compareMode === 'mom') {
    const end = new Date(now.getTime() - days * 86400000);
    const start = new Date(end.getTime() - days * 86400000);
    return { start, end };
  }
  if (compareMode === 'yoy_year') {
    const end = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    const start = new Date(end.getTime() - days * 86400000);
    return { start, end };
  }
  if (compareMode === 'yoy_month') {
    const end = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const start = new Date(end.getTime() - days * 86400000);
    return { start, end };
  }
  if (compareMode === 'yoy_week') {
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    const start = new Date(end.getTime() - days * 86400000);
    return { start, end };
  }
  return null;
}

function filterByCompareRange(records, range) {
  if (!range) return [];
  return records.filter(r => {
    const d = new Date(r.recorded_at);
    return d >= range.start && d < range.end;
  });
}

function getCompareLabel(timeRange, compareMode, customDays) {
  if (compareMode === 'none') return '';
  const days = getDaysFromTimeRange(timeRange, customDays);
  if (timeRange === 'today') {
    if (compareMode === 'mom') return '昨日';
    if (compareMode === 'yoy_year') return '去年今日';
    if (compareMode === 'yoy_month') return '上月今日';
    if (compareMode === 'yoy_week') return '上周今日';
  }
  if (compareMode === 'mom') return `前${days}天`;
  if (compareMode === 'yoy_year') return `去年同${days}天`;
  if (compareMode === 'yoy_month') return `上月同${days}天`;
  if (compareMode === 'yoy_week') return `上周同${days}天`;
  return '';
}

function filterByChildNodes(records, childNodes, projectName) {
  if (!childNodes || childNodes.length === 0) return records;
  return records.filter(r => childNodes.includes(getDirectChild(r.node_path, projectName)));
}

function calcStat(records, statType) {
  if (!records || records.length === 0) return null;
  if (statType === 'count') return records.length;
  const withVal = records.filter(r => r.numeric_value != null && r.numeric_value !== '');
  if (withVal.length === 0) return null;
  const nums = withVal.map(r => parseFloat(r.numeric_value));
  switch (statType) {
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg': return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min': return Math.min(...nums);
    case 'max': return Math.max(...nums);
    default: return null;
  }
}

function fmtNum(val) {
  if (val === null || val === undefined) return '--';
  if (Number.isInteger(val)) return val.toLocaleString();
  if (Math.abs(val) >= 100) return val.toFixed(1);
  return val.toFixed(2);
}

function getCommonUnit(records) {
  const r = records.find(r => r.numeric_unit);
  return r ? r.numeric_unit : '';
}

function calcChangeRate(current, previous) {
  if (current === null || previous === null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function calcChangeValue(current, previous) {
  if (current === null || previous === null) return null;
  return current - previous;
}

function processNumberData(allRecords, config, projectName) {
  const timeFiltered = filterByTimeRange(allRecords, config.timeRange, config.customDays);
  const filtered = filterByChildNodes(timeFiltered, config.childNodes, projectName);
  const value = calcStat(filtered, config.statType);
  const unit = config.statType === 'count' ? '次' : getCommonUnit(filtered);
  const result = { value, unit, compareValue: null, compareLabel: '', changeRate: null, changeValue: null };
  if (config.compareMode && config.compareMode !== 'none') {
    const range = getCompareRange(config.timeRange, config.compareMode, config.customDays);
    const compareFiltered = filterByChildNodes(filterByCompareRange(allRecords, range), config.childNodes, projectName);
    result.compareValue = calcStat(compareFiltered, config.statType);
    result.compareLabel = getCompareLabel(config.timeRange, config.compareMode, config.customDays);
    result.changeRate = calcChangeRate(value, result.compareValue);
    result.changeValue = calcChangeValue(value, result.compareValue);
  }
  return result;
}

function processPieData(allRecords, config, projectName) {
  const timeFiltered = filterByTimeRange(allRecords, config.timeRange, config.customDays);
  const filtered = filterByChildNodes(timeFiltered, config.childNodes, projectName);
  const groups = {};
  filtered.forEach(r => {
    const child = getDirectChild(r.node_path, projectName) || '其他';
    if (!groups[child]) groups[child] = [];
    groups[child].push(r);
  });
  return Object.entries(groups).map(([name, recs]) => ({
    name,
    value: calcStat(recs, config.statType) || 0,
  }));
}

function buildHourlyData(filtered, config, projectName) {
  const sorted = [...filtered].sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));
  const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);
  const hourGroups = {};
  hours.forEach(h => { hourGroups[h] = []; });
  sorted.forEach(r => {
    const d = new Date(r.recorded_at);
    const hKey = `${d.getHours()}:00`;
    if (hourGroups[hKey]) hourGroups[hKey].push(r);
  });
  if (config.splitByChildNode) {
    const childSet = [...new Set(sorted.map(r => getDirectChild(r.node_path, projectName)).filter(Boolean))];
    return {
      dates: hours,
      series: childSet.map(child => ({
        name: child,
        data: hours.map(h => calcStat(hourGroups[h].filter(r => getDirectChild(r.node_path, projectName) === child), config.statType) || 0),
      })),
    };
  }
  return {
    dates: hours,
    series: [{
      name: STAT_TYPES.find(s => s.value === config.statType)?.label || '',
      data: hours.map(h => calcStat(hourGroups[h], config.statType) || 0),
    }],
  };
}

function buildDailyData(filtered, config, projectName) {
  const groups = {};
  filtered.forEach(r => {
    const d = new Date(r.recorded_at);
    const key = `${d.getMonth() + 1}/${d.getDate()}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const [am, ad] = a.split('/').map(Number);
    const [bm, bd] = b.split('/').map(Number);
    return (am * 100 + ad) - (bm * 100 + bd);
  });
  if (config.splitByChildNode) {
    const childSet = [...new Set(filtered.map(r => getDirectChild(r.node_path, projectName)).filter(Boolean))];
    return {
      dates: sortedKeys,
      series: childSet.map(child => ({
        name: child,
        data: sortedKeys.map(k => calcStat(groups[k].filter(r => getDirectChild(r.node_path, projectName) === child), config.statType) || 0),
      })),
    };
  }
  return {
    dates: sortedKeys,
    series: [{
      name: STAT_TYPES.find(s => s.value === config.statType)?.label || '',
      data: sortedKeys.map(k => calcStat(groups[k], config.statType) || 0),
    }],
  };
}

function processTimelineData(allRecords, config, projectName) {
  const timeFiltered = filterByTimeRange(allRecords, config.timeRange, config.customDays);
  const filtered = filterByChildNodes(timeFiltered, config.childNodes, projectName);
  const current = config.timeRange === 'today'
    ? buildHourlyData(filtered, config, projectName)
    : buildDailyData(filtered, config, projectName);

  if (config.compareMode && config.compareMode !== 'none') {
    const range = getCompareRange(config.timeRange, config.compareMode, config.customDays);
    const compareFiltered = filterByChildNodes(filterByCompareRange(allRecords, range), config.childNodes, projectName);
    const compare = config.timeRange === 'today'
      ? buildHourlyData(compareFiltered, config, projectName)
      : buildDailyData(compareFiltered, config, projectName);
    const compareLabel = getCompareLabel(config.timeRange, config.compareMode, config.customDays);
    const mergedSeries = [];
    current.series.forEach(s => {
      mergedSeries.push({ ...s, name: s.name, isCompare: false });
      const matchCompare = compare.series.find(cs => cs.name === s.name);
      if (matchCompare) {
        mergedSeries.push({ ...matchCompare, name: `${s.name}(${compareLabel})`, isCompare: true });
      }
    });
    if (current.series.length === 1 && compare.series.length === 1 && current.series[0].name !== compare.series[0].name) {
      mergedSeries.push({ ...compare.series[0], name: `${compare.series[0].name}(${compareLabel})`, isCompare: true });
    }
    return { dates: current.dates, series: mergedSeries };
  }

  return current;
}

function buildPieOption(data, isSmall) {
  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    color: COLORS,
    series: [{
      type: 'pie',
      radius: isSmall ? ['25%', '55%'] : ['35%', '65%'],
      center: ['50%', '55%'],
      data,
      label: { fontSize: isSmall ? 9 : 11, formatter: '{b}\n{d}%' },
      labelLine: { length: 6, length2: 6 },
      emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.2)' } },
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
    }],
  };
}

function buildLineBarOption(data, chartType, isSmall) {
  let colorIdx = 0;
  const series = data.series.map((s) => {
    const color = COLORS[colorIdx % COLORS.length];
    colorIdx++;
    if (s.isCompare) {
      if (chartType === 'line') {
        return {
          name: s.name,
          type: 'line',
          data: s.data,
          smooth: true,
          symbolSize: 3,
          color,
          lineStyle: { type: 'dashed', width: 2 },
          itemStyle: { color },
        };
      }
      return {
        name: s.name,
        type: 'bar',
        data: s.data,
        color,
        itemStyle: { borderRadius: [4, 4, 0, 0], opacity: 0.4 },
        barGap: '0%',
      };
    }
    return {
      name: s.name,
      type: chartType === 'line' ? 'line' : 'bar',
      data: s.data,
      smooth: chartType === 'line',
      symbolSize: 4,
      color,
      ...(chartType === 'line' ? { areaStyle: { opacity: 0.12 } } : { itemStyle: { borderRadius: [4, 4, 0, 0] } }),
    };
  });
  return {
    tooltip: { trigger: 'axis' },
    color: COLORS,
    legend: data.series.length > 2 ? { type: 'scroll', bottom: 0, textStyle: { fontSize: isSmall ? 8 : 10 } } : undefined,
    grid: { left: '14%', right: '6%', top: data.series.length > 2 ? '16%' : '12%', bottom: isSmall ? '22%' : '16%' },
    xAxis: {
      type: 'category',
      data: data.dates,
      axisLabel: { fontSize: isSmall ? 8 : 10, rotate: data.dates.length > 8 ? 30 : 0 },
      axisLine: { lineStyle: { color: 'rgba(0,0,0,0.1)' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: isSmall ? 8 : 10 },
      splitLine: { lineStyle: { color: 'rgba(0,0,0,0.05)' } },
    },
    series,
  };
}

function fmtChangeNum(val) {
  if (val === null || val === undefined) return '--';
  const abs = Math.abs(val);
  if (Number.isInteger(abs)) return abs.toLocaleString();
  if (abs >= 100) return abs.toFixed(1);
  return abs.toFixed(2);
}

function WidgetCard({ config, records, projectName, isMobile, onEdit, onDelete }) {
  const content = useMemo(() => {
    const hasData = records && records.length > 0;
    if (!hasData) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(0,0,0,0.2)', fontSize: 13 }}>
          暂无数据
        </div>
      );
    }

    switch (config.chartType) {
      case 'number': {
        const data = processNumberData(records, config, projectName);
        const hasCompare = config.compareMode && config.compareMode !== 'none';
        const isSmall = config.size === 'small';
        const isWide = config.size === 'wide';
        const isThin = config.size === 'thin';
        if (hasCompare) {
          const isUp = (data.changeValue || 0) > 0;
          const isZero = (data.changeValue || 0) === 0;
          const changeColor = isZero ? 'rgba(0,0,0,0.3)' : isUp ? '#FF3B30' : '#34C759';
          const arrow = isZero ? '→' : isUp ? '↑' : '↓';
          const mainSize = isThin ? 10 : isSmall ? 14 : isWide ? 18 : 20;
          const changeSize = isThin ? 12 : isSmall ? 16 : isWide ? 22 : 28;
          const subSize = isThin ? 8 : isSmall ? 9 : 11;
          const currentLabel = config.timeRange === 'today' ? '今日' : config.timeRange === 'custom' ? `近${config.customDays || 7}天` : TIME_RANGES.find(t => t.value === config.timeRange)?.label || '';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: changeSize, color: changeColor, fontWeight: 700, lineHeight: 1.2 }}>
                {arrow} {fmtChangeNum(data.changeValue)} {data.unit}
                {data.changeRate !== null && (
                  <span style={{ fontSize: mainSize, fontWeight: 600, marginLeft: 4 }}>({Math.abs(data.changeRate).toFixed(1)}%)</span>
                )}
              </div>
              <div style={{ fontSize: mainSize, color: '#1c1c1e', fontWeight: 600, marginTop: 4, lineHeight: 1.2 }}>
                {currentLabel} {fmtNum(data.value)} <span style={{ fontSize: subSize, fontWeight: 500, color: 'rgba(0,0,0,0.4)' }}>{data.unit}</span>
              </div>
              <div style={{ fontSize: subSize, color: 'rgba(0,0,0,0.35)', marginTop: 3, fontWeight: 500 }}>
                vs{data.compareLabel} {fmtNum(data.compareValue)} {data.unit}
              </div>
            </div>
          );
        }
        const fontSize = isThin ? 18 : isSmall ? 28 : isWide ? 38 : 44;
        const unitSize = isThin ? 9 : isSmall ? 10 : 12;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
            <div style={{ fontSize, fontWeight: 700, color: '#1c1c1e', lineHeight: 1.2, letterSpacing: -1 }}>
              {fmtNum(data.value)}
            </div>
            <div style={{ fontSize: unitSize, color: 'rgba(0,0,0,0.4)', marginTop: 2, fontWeight: 500 }}>
              {data.unit}
            </div>
          </div>
        );
      }
      case 'pie': {
        const data = processPieData(records, config, projectName);
        if (!data || data.length === 0) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(0,0,0,0.2)' }}>暂无数据</div>;
        const isSmall = config.size === 'small' || config.size === 'thin';
        const option = buildPieOption(data, isSmall);
        return <ReactECharts option={option} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge />;
      }
      case 'line':
      case 'bar': {
        const data = processTimelineData(records, config, projectName);
        if (!data.dates || data.dates.length === 0) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'rgba(0,0,0,0.2)' }}>暂无数据</div>;
        const isSmall = config.size === 'small' || config.size === 'wide' || config.size === 'thin';
        const option = buildLineBarOption(data, config.chartType, isSmall);
        return <ReactECharts option={option} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge />;
      }
      default:
        return null;
    }
  }, [records, config, projectName]);

  return (
    <div className="widget-card">
      <div className="widget-card-header">
        <span className="widget-card-title">{config.title}</span>
        <div className="widget-card-actions">
          <Tooltip title="编辑">
            <button className="widget-card-btn" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              <EditOutlined />
            </button>
          </Tooltip>
          <Popconfirm title="确定删除此图表？" onConfirm={(e) => { e && e.stopPropagation(); onDelete(); }} onCancel={(e) => e && e.stopPropagation()}>
            <button className="widget-card-btn widget-card-btn-danger" onClick={(e) => e.stopPropagation()}>
              <DeleteOutlined />
            </button>
          </Popconfirm>
          <span className="drag-handle widget-card-btn widget-card-btn-drag">
            <HolderOutlined />
          </span>
        </div>
      </div>
      <div className="widget-card-content">
        {content}
      </div>
    </div>
  );
}

function EditCardModal({ visible, card, childNodes, onSave, onCancel }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (visible) {
      setForm(card ? { ...card } : {
        title: '',
        size: 'small',
        chartType: 'number',
        statType: 'count',
        childNodes: [],
        timeRange: 'today',
        customDays: 7,
        splitByChildNode: false,
        compareMode: 'none',
      });
    }
  }, [visible, card]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = () => {
    if (!form.title?.trim()) { message.warning('请输入标题'); return; }
    onSave({ ...form, title: form.title.trim() });
  };

  const showSplit = form.chartType === 'line' || form.chartType === 'bar';
  const showCompare = form.chartType === 'number' || form.chartType === 'line' || form.chartType === 'bar';

  return (
    <Modal
      title={card?.id ? '编辑图表' : '添加图表'}
      open={visible}
      onOk={handleSave}
      onCancel={onCancel}
      okText="保存"
      cancelText="取消"
      destroyOnClose
      width={380}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0' }}>
        <div>
          <div className="form-label">标题</div>
          <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="" />
        </div>
        <div>
          <div className="form-label">卡片大小</div>
          <Select value={form.size} onChange={v => set('size', v)} style={{ width: '100%' }}
            options={Object.entries(SIZE_MAP).map(([k, v]) => ({ value: k, label: v.label }))} />
        </div>
        <div>
          <div className="form-label">图表类型</div>
          <Select value={form.chartType} onChange={v => set('chartType', v)} style={{ width: '100%' }} options={CHART_TYPES} />
        </div>
        <div>
          <div className="form-label">统计类型</div>
          <Select value={form.statType} onChange={v => set('statType', v)} style={{ width: '100%' }} options={STAT_TYPES} />
        </div>
        <div>
          <div className="form-label">子节点筛选</div>
          <Select
            mode="multiple"
            value={form.childNodes || []}
            onChange={v => set('childNodes', v)}
            style={{ width: '100%' }}
            placeholder="全部"
            options={childNodes.map(n => ({ value: n, label: n }))}
            allowClear
          />
        </div>
        <div>
          <div className="form-label">时间范围</div>
          <Select value={form.timeRange} onChange={v => set('timeRange', v)} style={{ width: '100%' }} options={TIME_RANGES} />
        </div>
        {form.timeRange === 'custom' && (
          <div>
            <div className="form-label">自定义天数</div>
            <InputNumber min={1} value={form.customDays || 7} onChange={v => set('customDays', v)} style={{ width: '100%' }} addonAfter="天" />
          </div>
        )}
        {showCompare && (
          <div>
            <div className="form-label">对比</div>
            <Select value={form.compareMode || 'none'} onChange={v => set('compareMode', v)} style={{ width: '100%' }} options={COMPARE_MODES} />
          </div>
        )}
        {showSplit && (
          <div>
            <Checkbox checked={form.splitByChildNode} onChange={e => set('splitByChildNode', e.target.checked)}>
              按子节点分系列显示
            </Checkbox>
          </div>
        )}
      </div>
    </Modal>
  );
}

const StatsPage = ({ isMobile: isMobileProp }) => {
  const user = getUser();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [records, setRecords] = useState([]);
  const [cardConfigs, setCardConfigs] = useState([]);
  const [savedLayouts, setSavedLayouts] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [isMobile, setIsMobile] = useState(isMobileProp || window.innerWidth < 768);
  const layoutRef = useRef(null);
  const containerRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(140);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const updateRowHeight = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const cols = containerWidth >= 996 ? 8 : containerWidth >= 600 ? 4 : 2;
      const marginX = 12;
      const colWidth = (containerWidth - (cols - 1) * marginX) / cols;
      setRowHeight(Math.round(colWidth / 2));
    };
    updateRowHeight();
    const observer = new ResizeObserver(updateRowHeight);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    api.get('/projects').then(res => {
      const list = res.data.filter(p => !p.is_template);
      setProjects(list);
      if (!selectedProject && list.length) setSelectedProject(list[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const res = await api.get('/records', { params: { projectId: selectedProject } });
      setRecords(Array.isArray(res.data) ? res.data : []);
    } catch { setRecords([]); }
  }, [selectedProject]);

  useEffect(() => { if (selectedProject) loadData(); }, [selectedProject, loadData]);

  useEffect(() => {
    if (!selectedProject) return;
    api.get(`/projects/${selectedProject}/stats-config`).then(res => {
      setCardConfigs(Array.isArray(res.data) ? res.data : []);
    }).catch(() => { setCardConfigs([]); });
    const allLayouts = loadJson(LAYOUT_KEY);
    const projectLayouts = allLayouts[selectedProject] || {};
    // Migrate old layout h values (old rowHeight was full colWidth, now is half)
    const migrated = {};
    Object.entries(projectLayouts).forEach(([bp, items]) => {
      migrated[bp] = items.map(item => ({
        ...item,
        h: item.h <= 2 ? item.h * 2 : item.h,
      }));
    });
    setSavedLayouts(migrated);
  }, [selectedProject]);

  const persistConfigs = useCallback((configs) => {
    if (!selectedProject) return;
    setCardConfigs(configs);
    api.put(`/projects/${selectedProject}/stats-config`, configs).catch(() => {
      message.error('保存配置失败');
    });
  }, [selectedProject]);

  const persistLayouts = useCallback((layouts) => {
    if (!selectedProject) return;
    const all = loadJson(LAYOUT_KEY);
    all[selectedProject] = layouts;
    saveJson(LAYOUT_KEY, all);
    setSavedLayouts(layouts);
  }, [selectedProject]);

  const projectName = useMemo(() => {
    return projects.find(p => p.id === selectedProject)?.name || '';
  }, [projects, selectedProject]);

  const childNodes = useMemo(() => {
    const names = new Set();
    records.forEach(r => {
      const child = getDirectChild(r.node_path, projectName);
      if (child) names.add(child);
    });
    return [...names].sort();
  }, [records, projectName]);

  const handleAdd = () => {
    setEditingCard(null);
    setModalVisible(true);
  };

  const handleEdit = (cardId) => {
    const card = cardConfigs.find(c => c.id === cardId);
    if (card) { setEditingCard({ ...card }); setModalVisible(true); }
  };

  const handleDelete = (cardId) => {
    persistConfigs(cardConfigs.filter(c => c.id !== cardId));
    message.success('已删除');
  };

  const handleSaveCard = (form) => {
    if (editingCard?.id) {
      persistConfigs(cardConfigs.map(c => c.id === editingCard.id ? { ...c, ...form } : c));
    } else {
      persistConfigs([...cardConfigs, { ...form, id: `w-${Date.now()}` }]);
    }
    setModalVisible(false);
    setEditingCard(null);
    message.success(editingCard?.id ? '已更新' : '已添加');
  };

  const builtLayouts = useMemo(() => {
    const build = (cols) => {
      const result = [];
      const bp = cols >= 8 ? 'lg' : cols >= 4 ? 'md' : 'sm';
      cardConfigs.forEach((config, idx) => {
        const sizeInfo = SIZE_MAP[config.size] || SIZE_MAP.small;
        const w = Math.min(sizeInfo.w, cols);
        const h = sizeInfo.h;
        const saved = savedLayouts[bp]?.find(l => l.i === config.id);
        if (saved) { result.push({ ...saved, w, h }); return; }
        let x = 0;
        let y = 0;
        if (idx > 0) {
          const prev = result[idx - 1];
          x = prev.x + prev.w;
          if (x + w > cols) { x = 0; y = prev.y + prev.h; } else { y = prev.y; }
        }
        result.push({ i: config.id, x, y, w, h });
      });
      return result;
    };
    return { lg: build(8), md: build(4), sm: build(2) };
  }, [cardConfigs, savedLayouts]);

  const handleLayoutChange = useCallback((_current, allLayouts) => {
    persistLayouts(allLayouts);
  }, [persistLayouts]);

  if (!user) return <div style={{ padding: 24, textAlign: 'center' }}>请先登录</div>;

  return (
    <div ref={containerRef} style={{ padding: isMobile ? 12 : 24, minHeight: '100vh', background: '#f2f2f7' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 0 }}>
          <Select
            value={selectedProject}
            onChange={setSelectedProject}
            style={{ width: isMobile ? '100%' : 200, flexShrink: 0 }}
            options={projects.map(p => ({ value: p.id, label: p.name }))}
            placeholder="选择项目"
          />
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedProject}>
          添加图表
        </Button>
      </div>

      {cardConfigs.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Empty description="暂无图表，点击「添加图表」创建" />
        </div>
      ) : (
        <ResponsiveGridLayout
          key={selectedProject}
          ref={layoutRef}
          layouts={builtLayouts}
          breakpoints={{ lg: 996, md: 600, sm: 0 }}
          cols={{ lg: 8, md: 4, sm: 2 }}
          rowHeight={rowHeight}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          onLayoutChange={handleLayoutChange}
          isDraggable
          isResizable={false}
          draggableHandle=".drag-handle"
          compactType="vertical"
          useCSSTransforms
        >
          {cardConfigs.map(config => (
            <div key={config.id}>
              <WidgetCard
                config={config}
                records={records}
                projectName={projectName}
                isMobile={isMobile}
                onEdit={() => handleEdit(config.id)}
                onDelete={() => handleDelete(config.id)}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <EditCardModal
        visible={modalVisible}
        card={editingCard}
        childNodes={childNodes}
        onSave={handleSaveCard}
        onCancel={() => { setModalVisible(false); setEditingCard(null); }}
      />
    </div>
  );
};

export default StatsPage;
